import styled, { keyframes } from 'styled-components';
import { lighten, darken } from 'polished';
import { withStyles } from '@material-ui/core/styles';
import MuiClose from '@material-ui/icons/Close';
import MuiDoubleArrow from '@material-ui/icons/DoubleArrow';
import MuiRadioGroup from '@material-ui/core/RadioGroup';
import MuiRadio from '@material-ui/core/Radio';
import MuiFormControlLabel from '@material-ui/core/FormControlLabel';
//
import colors from 'theme/colors';
import Button from 'components/buttons/simple';
import TextButton from 'components/buttons/text';
import { IEditLabelProps, ILabelProps } from './types';

const {
  darkBackgroundLightBorder,
  darkBorder,
  darkPanelBackground,
  error,
  fontBody,
  labelPlatinum,
  lightBackgroundBase,
  platinum,
  yellow,
} = colors;

const colorGradient = 0.2;

export const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: ${fontBody};
`;

export const InputGroup = styled.div`
  margin-bottom: 8px;
`;

export const InputGroupHeader = styled.p`
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  min-height: 16px;
`;

export const InputGroupRow = styled.div`
  display: flex;
  justify-content: space-between;

  ${InputGroup}:nth-child(1) {
    width: 130px;
  }

  ${InputGroup}:nth-child(2) {
    width: 136px;
  }
`;

export const CostsInputGroupRow = styled.div`
  display: flex;
  justify-content: space-between;

  ${InputGroup} {
    width: 180px;
  }
`;

export const InputError = styled.div`
  color: ${error};
  font-size: 12px;
  margin-top: 8px;
  min-height: 10px;
`;

export const TitleContainer = styled.div`
  text-align: center;
  position: relative;
`;

export const Title = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${platinum};
  margin: 10px 0px;
`;

export const CloseButton = styled.div`
  position: absolute;
  right: 0;
  top: 0;
`;

export const Content = styled.div`
  height: 500px;
`;

export const ContentWithScroll = styled(Content)`
  overflow-y: scroll;
  padding: 8px;
`;

export const ContentSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

export const VolumeOptionModalSpacing = styled.div`
  height: 32px;
`;

export const OptionSectionPanel = styled.div`
  width: 276px;
`;

export const CostsSectionPanel = styled.div`
  width: calc(50% - 40px);
  padding-bottom: 16px;
  padding-right: 40px;
  border-right: 1px solid ${darkBackgroundLightBorder};

  input {
    width: 180px;
  }

  :nth-of-type(2) {
    padding-left: 40px;
    padding-right: 0px;
    border: none;
  }
`;

export const ContentSectionPanel = styled.div`
  width: 48%;
`;

export const ScrollableContentSectionPanel = styled(ContentSectionPanel)`
  overflow-y: scroll;
  height: 475px;
  padding-right: 8px;
`;

export const ScrollableContentSectionPanelCosts = styled(ScrollableContentSectionPanel)`
  padding-right: 32px;

  input {
    width: 250px;
  }
`;

export const LargeSection = styled.div`
  margin: 0px 92px;
`;

export const InformationPanel = styled(ContentSectionPanel)`
  color: ${labelPlatinum};
  margin-right: 32px;
  font-size: 16px;
`;

export const ContentSeparator = styled.div`
  text-align: center;
  margin: auto;
  margin-top: 40px;
  margin-bottom: 140px;
  width: 100%;
  font-size: 12px;
  color: ${labelPlatinum};

  div {
    display: flex;
    align-items: center;
    justify-content: center;

    span {
      padding-left: 4px;
    }
  }
`;

export const ContentSeparatorContractTerm = styled(ContentSeparator)`
  margin-top: 16px;
`;

const pulseAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0; }
  100% { opacity: 1; }
`;

export const DoubleArrowIcon = styled(MuiDoubleArrow)`
  color: ${yellow};
  transform: rotate(90deg);
  animation: ${pulseAnimation} infinite 2.5s ease-in-out;
`;

export const ButtonContainer = styled.div`
  border-top: 1px solid ${darkBackgroundLightBorder};
  padding-top: 16px;
  text-align: right;
`;

export const EditLabel = styled.label<IEditLabelProps>`
  display: block;
  font-size: 12px;
  font-style: italic;
  font-weight: 600;
  margin-bottom: 8px;
  margin-top: 8px;
  color: ${props => (props['data-valid'] ? error : platinum)};
`;

export const Label = styled.label<ILabelProps>`
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
  color: ${props => (props['data-valid'] ? error : platinum)};
`;

export const SaveFormulaButton = styled(Button)`
  background-color: ${yellow};
  color: ${darkBorder};
  margin-left: 8px;
`;

export const CancelButton = styled(Button)`
  border: 1px solid ${lightBackgroundBase};
  width: 80px;
  background-color: ${darkPanelBackground};

  &:hover {
    background-color: ${darken(colorGradient, darkPanelBackground)};
  }
`;

export const CloseIcon = styled(MuiClose)`
  color: ${platinum};
  position: absolute;
  right: 0;
  top: 0;

  &:hover {
    cursor: pointer;
    color: ${darken(colorGradient, platinum)};
  }
`;

export const RadioGroup = withStyles({
  root: {
    flexDirection: 'row',
  },
})(MuiRadioGroup);

export const Radio = withStyles({
  root: {
    '&:hover': {
      backgroundColor: lighten(colorGradient, labelPlatinum),
    },
    '&.Mui-checked': {
      color: labelPlatinum,
    },
  },
})(MuiRadio);

export const FormControlLabel = withStyles({
  root: {
    '& .MuiFormControlLabel-label': {
      fontSize: 14,
      color: labelPlatinum,
    },
    '& .MuiRadio-root': {
      padding: 6,
      color: labelPlatinum,

      '&:hover': {
        color: darken(colorGradient, labelPlatinum),
        background: 'none',
      },
    },
  },
})(MuiFormControlLabel);

export const MarkerSearchBoxContainer = styled.div`
  margin-bottom: 16px;
`;

export const RemoveBatnaTextButton = styled(TextButton)({
  color: lightBackgroundBase,
  textDecoration: 'underline',
  fontSize: 14,
  '&:hover': {
    color: darken(colorGradient, lightBackgroundBase),
  },
});

export const ShowMoreButton = styled(TextButton)`
  position: relative;
  top: -8px;
  text-decoration: underline;
  color: ${labelPlatinum};

  &:hover {
    color: ${darken(colorGradient, labelPlatinum)};
  }
`;

export const ContentGroup = styled.div`
  margin: 0px 0px 16px;

  label {
    margin-bottom: 8px;
  }
`;

export const ParetoModalContent = styled(Content)`
  margin-top: 48px;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

export const ParetoModalContentLeft = styled.div`
  width: 192px;
`;

export const ParetoModalContentRight = styled.div`
  width: 500px;
`;

export const NoDealsMessage = styled.div`
  text-align: center;
  margin-top: 150px;
`;

export const InputWrapper = styled(InputGroup)`
  width: 280px;
  margin-bottom: 20px;
`;
